const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/** Activity summary returned within promotion code responses. */
interface PromotionCodeActivityRef {
  id: string;
  activity: { id: string; titleCN: string | null; titleEN: string | null };
}

/** Promotion code as returned by admin list/create/update endpoints. */
export interface PromotionCode {
  id: string;
  code: string;
  description: string | null;
  discountType: 'FIXED_AMOUNT' | 'PERCENTAGE';
  discountValue: number;
  maxUsesPerUser: number | null;
  validFrom: string | null;
  validUntil: string | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  activities: PromotionCodeActivityRef[];
  creator: { id: string; name: string | null };
  _count: { usages: number };
}

/** Promotion code detail returned by getById, including usages. */
export interface PromotionCodeDetail extends Omit<PromotionCode, '_count'> {
  usages: {
    id: string;
    promotionCodeId: string;
    activityId: string;
    userId: string;
    bookingId: string;
    discountAmount: number;
    email: string;
    createdAt: string;
    user: { id: string; name: string | null; email: string };
    activity: { id: string; titleCN: string | null; titleEN: string | null };
    booking: { id: string; totalAmount: number; status: string; createdAt: string };
  }[];
}

class PromotionCodeApi {
  // User-facing: validate a promotion code (supports guests via email).
  async validate(
    code: string,
    sessionId: string,
    email: string,
    token?: string
  ): Promise<{
    valid: boolean;
    promotionCodeId?: string;
    discountType?: 'FIXED_AMOUNT' | 'PERCENTAGE';
    discountValue?: number;
    reason?: string;
    message: string;
    isMemberCode?: boolean;
  }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/promotion-codes/validate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code, sessionId, email }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Safety net for rare token-with-no-email-claim edge case.
        return { valid: false, reason: 'AUTH_REQUIRED', message: '请登录后使用优惠码' };
      }
      if (response.status === 429) {
        return { valid: false, reason: 'RATE_LIMITED', message: '请求过于频繁，请稍后再试' };
      }
      const error = await response.json();
      throw new Error(error.error?.message || error.error || 'Failed to validate promotion code');
    }

    const result = await response.json();
    return result.data;
  }

  // Admin: get all promotion codes
  async getAll(
    token: string,
    params?: { search?: string; page?: number; limit?: number }
  ): Promise<{ items: PromotionCode[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();

    const response = await fetch(`${API_URL}/admin/promotion-codes${qs ? `?${qs}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error('Failed to fetch promotion codes');
    const result = await response.json();
    return result.data;
  }

  // Admin: get by ID
  async getById(id: string, token: string): Promise<PromotionCodeDetail> {
    const response = await fetch(`${API_URL}/admin/promotion-codes/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error('Failed to fetch promotion code');
    const result = await response.json();
    return result.data;
  }

  // Admin: create
  async create(
    data: {
      code: string;
      description?: string;
      discountType: 'FIXED_AMOUNT' | 'PERCENTAGE';
      discountValue: number;
      maxUsesPerUser?: number | null;
      validFrom?: string | null;
      validUntil?: string | null;
      activityIds: string[];
    },
    token: string
  ): Promise<PromotionCode> {
    const response = await fetch(`${API_URL}/admin/promotion-codes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || error.error || 'Failed to create promotion code');
    }
    const result = await response.json();
    return result.data;
  }

  // Admin: update
  async update(
    id: string,
    data: {
      description?: string;
      maxUsesPerUser?: number | null;
      validFrom?: string | null;
      validUntil?: string | null;
      isActive?: boolean;
      activityIds?: string[];
    },
    token: string
  ): Promise<PromotionCode> {
    const response = await fetch(`${API_URL}/admin/promotion-codes/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || error.error || 'Failed to update promotion code');
    }
    const result = await response.json();
    return result.data;
  }
}

export const promotionCodeApi = new PromotionCodeApi();
